/**
 * Carte d'une unité de connaissance (Base de connaissances).
 * Extrait de KnowledgePage.js (refactor 2026-06-12) — zéro changement de logique.
 */
import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { VideoCamera, Clock, CaretRight, X, Heart, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { TYPE_STYLES, LIBELLES_TYPE, formatRelativeTime } from "../../lib/knowledgeFormat";
// Vincent B702 (Eric 2026-02-11) — Palette timeline vidéo appliquée en
// bordure gauche de chaque carte KU pour permettre l'identification
// visuelle du type même en scroll rapide.
import { TYPE_COLORS } from "../../lib/videoDetailFormat";
// Eric 2026-07-30 (B609 enhancement, cohérence UX Base de connaissances) —
// Highlight partagé pour surligner les matches de recherche dans le titre +
// la question/réponse. Réutilisé de FileExplorerPage.js et JargonPage.js.
import { HighlightedText } from "../../lib/highlight";

export default function KnowledgeCard({ unit, videoTitle, formatTimestamp, onClick, isFavorite, onToggleFavorite, onTitleRegenerated, searchQuery = "" }) {
  // Vincent #135 (Eric 2026-05-13) — Navigation vers la vidéo source + lang i18n.
  const navigate = useNavigate();
  const { lang, tr } = useLang();
  // Vincent 2026-05-06 — Bouton ✨ rapide pour regenerer le titre d'une Q&R
  // SANS avoir besoin d'entrer en mode edition (bug #44)
  const [regenTitling, setRegenTitling] = useState(false);
  const handleQuickRegenTitle = async (e) => {
    e.stopPropagation();
    if (regenTitling) return;
    setRegenTitling(true);
    try {
      const { data } = await axios.post(
        `${API}/api/agent/knowledge-units/${unit.id}/generate-title`,
        {},
        { withCredentials: true, timeout: 30000 }
      );
      if (data?.title) {
        // Persiste direct (pas de workflow de revue — plus rapide)
        await axios.patch(
          `${API}/api/agent/knowledge-units/${unit.id}`,
          { title: data.title },
          { withCredentials: true }
        );
        toast.success(`Titre régénéré : ${data.title.slice(0, 50)}...`);
        if (onTitleRegenerated) onTitleRegenerated(unit.id, data.title);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Échec régénération titre", "Title regeneration failed"));
    } finally {
      setRegenTitling(false);
    }
  };

  const renderContent = () => {
    // Eric 2026-07-30 (B609 enhancement) — Highlight helper local, pour éviter
    // de répéter <HighlightedText> à chaque champ tout en gardant le rendu clair.
    const H = (text) => <HighlightedText text={text} query={searchQuery} testid={`ku-content-hl-${unit.id}`} />;
    switch (unit.type) {
      case "qa":
        return (
          <>
            {/* Vincent 2026-06-03 (Eric) — Q: pour question, R: pour réponse en français (pas "A:"). */}
            <p className="text-sm font-medium mb-1">Q&nbsp;: {H(unit.content?.question)}</p>
            <p className="text-sm text-zinc-600 line-clamp-2">{lang === "fr" ? "R" : "A"}&nbsp;: {H(unit.content?.answer)}</p>
          </>
        );
      case "procedure":
        return (
          <>
            <p className="text-sm text-zinc-600">
              {unit.content?.steps?.length || 0} steps
            </p>
            {unit.content?.steps?.[0] && (
              <p className="text-sm text-zinc-500 line-clamp-1 mt-1">
                1. {H(unit.content.steps[0])}
              </p>
            )}
          </>
        );
      case "script":
        return (
          <>
            <p className="text-sm text-zinc-600 line-clamp-1">
              Scénario : {H(unit.content?.scenario)}
            </p>
            {unit.content?.response && (
              <p className="text-sm text-zinc-500 line-clamp-1 mt-1">
                Réponse : {H(unit.content.response)}
              </p>
            )}
          </>
        );
      case "objection":
        // Vincent #145 (Eric 2026-05-25) — affiche l'objection et la phrase
        // d'évitement (au lieu de "scenario" qui n'existait pas dans le doc).
        return (
          <>
            <p className="text-sm font-medium mb-1 line-clamp-2">
              {lang === "fr" ? "Objection" : "Objection"}: {H(unit.content?.objection || unit.content?.question || unit.content?.scenario || "—")}
            </p>
            {(unit.content?.response || unit.content?.phrase_evitement) && (
              <p className="text-sm text-zinc-600 line-clamp-2">
                {lang === "fr" ? "Réponse" : "Response"}: {H(unit.content?.response || unit.content?.phrase_evitement)}
              </p>
            )}
          </>
        );
      case "concept":
        return (
          <p className="text-sm text-zinc-600 line-clamp-3">
            {H(unit.content?.definition)}
          </p>
        );
      case "synthesis":
        // Vincent B719 (Eric 2026-02-11) — Rendu lisible pour les synthèses
        // au lieu du JSON brut (default). Affiche un aperçu du body markdown
        // (strippé) + le nombre de points clés à retenir.
        return (() => {
          const body = unit.content?.body || "";
          // Strip markdown pour l'aperçu (bold **, headers #, listes -).
          const preview = body
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .replace(/^#{1,6}\s+/gm, "")
            .replace(/^-\s+/gm, "• ")
            .replace(/\n{2,}/g, " · ")
            .replace(/\n/g, " ")
            .trim();
          const takeawaysCount = Array.isArray(unit.content?.key_takeaways) ? unit.content.key_takeaways.length : 0;
          return (
            <>
              <p className="text-sm text-zinc-600 line-clamp-3">{H(preview)}</p>
              {takeawaysCount > 0 && (
                <p className="text-xs text-teal-700 mt-1 font-medium">
                  {takeawaysCount} {lang === "fr" ? "point(s) clé(s) à retenir" : "key takeaway(s)"}
                </p>
              )}
            </>
          );
        })();
      default:
        return (
          <p className="text-sm text-zinc-600 line-clamp-3">
            {H(JSON.stringify(unit.content).slice(0, 150))}...
          </p>
        );
    }
  };

  // Vincent B702 (Eric 2026-02-11) — Bordure gauche de 4px reprenant la
  // couleur type (miroir des traits verticaux de la timeline vidéo).
  const typeColor = TYPE_COLORS[unit.type]?.color || null;
  return (
    <div
      onClick={onClick}
      className="bg-white border border-zinc-200 rounded-sm p-5 pl-4 cursor-pointer card-hover"
      style={typeColor ? { borderLeft: `4px solid ${typeColor}` } : undefined}
      data-testid={`knowledge-card-${unit.id}`}
      data-focus-target={unit.id}
    >
      {/* Vincent (Eric 2026-02-06) — Bandeau orphelin : si la connaissance a
          été détachée d'une vidéo supprimée (cf. fix #5 conservation), on
          affiche un bandeau bleu pour expliquer l'origine. */}
      {unit.detached_from_video_id && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-[11px] text-blue-800 flex items-start gap-2" data-testid={`knowledge-card-orphan-banner-${unit.id}`}>
          <span className="text-base leading-none">📎</span>
          <span className="flex-1">
            <strong>Connaissance orpheline</strong> — rattachée à la vidéo « {unit.detached_from_video_title || unit.detached_from_video_id?.slice(0, 8)} » qui a été supprimée
            {unit.detached_at && ` le ${new Date(unit.detached_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}`}
            {unit.detached_by && ` par ${unit.detached_by}`}.
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {/* Vincent B546 (Eric 2026-07-27) — Coeur favori EN HAUT À GAUCHE
                (déplacé du footer bas-droit selon spec Vincent 24/07/2026). */}
            {onToggleFavorite && (
              <button
                type="button"
                onClick={(e) => onToggleFavorite(e, unit.id)}
                className={`p-0.5 rounded transition-colors flex-shrink-0 ${isFavorite ? "text-rose-500" : "text-zinc-300 hover:text-rose-400"}`}
                data-testid={`fav-btn-${unit.id}`}
                title={isFavorite ? tr("Retirer des favoris", "Remove from favourites") : tr("Ajouter aux favoris", "Add to favourites")}
              >
                <Heart size={18} weight={isFavorite ? "fill" : "regular"} />
              </button>
            )}
            <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${TYPE_STYLES[unit.type]}`}>
              {/* Vincent 2026-06-03 — Affichage canonique du type (Q&R en français/anglais). */}
              {LIBELLES_TYPE[unit.type] ? tr(LIBELLES_TYPE[unit.type][0], LIBELLES_TYPE[unit.type][1]) : unit.type}
            </span>
            {/* Vincent #125 (Eric 2026-05-13) — Badge statut de validation. */}
            {unit.status === "pending_review" && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`status-badge-pending-${unit.id}`}>
                {tr("EN RÉVISION", "UNDER REVIEW")}
              </span>
            )}
            {unit.status === "approved" && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-teal-100 text-teal-800 border border-teal-200" data-testid={`status-badge-approved-${unit.id}`}>
                {tr("VALIDÉE", "VALIDATED")}
              </span>
            )}
            {/* Vincent #252 (2026-02-06) — Badge tr("REFUSÉE", "REJECTED") rouge symétrique
                à tr("EN RÉVISION", "UNDER REVIEW") (ambre) et tr("VALIDÉE", "VALIDATED") (vert). Une connaissance
                refusée reste visible dans la liste pour audit Loi 25, avec
                cette mention claire en rouge pour Vincent. */}
            {unit.status === "rejected" && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-rose-100 text-rose-800 border border-rose-200" data-testid={`status-badge-rejected-${unit.id}`}>
                {tr("REFUSÉE", "REJECTED")}
              </span>
            )}
            {unit.confidence && (
              /* Vincent B875-final (Eric 2026-02-14) — text-zinc-400 sur
                 bg-white affichait 2.56:1 (fail WCAG AA). zinc-500 monte
                 à ≈ 4.84:1 pour respecter le seuil texte normal AA. */
              <span className="text-xs text-zinc-500">
                {Math.round(unit.confidence * 100)}% confidence
              </span>
            )}
            {/* Eric (2026-05-18) — Badge "Édité par X il y a Y" pour distinguer
                d'un coup d'œil les fiches retravaillées par un humain (Vincent
                & co.) de celles encore brutes IA. Affiché seulement si la fiche
                a été éditée après création (last_edited_at présent). */}
            {unit.last_edited_at && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-800 border border-amber-200"
                data-testid={`last-edit-badge-${unit.id}`}
                title={`Édité le ${new Date(unit.last_edited_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}${unit.last_edited_by_email ? ` par ${unit.last_edited_by_email}` : ""}`}
              >
                <PencilSimple size={10} weight="fill" />
                {(() => {
                  const who = unit.last_edited_by_email
                    ? unit.last_edited_by_email.split("@")[0]
                    : (lang === "fr" ? "édité" : "edited");
                  return `${who} · ${formatRelativeTime(unit.last_edited_at, lang)}`;
                })()}
              </span>
            )}
          </div>
          <div className="flex items-start gap-1.5">
            <h3 className="font-medium text-sm leading-snug line-clamp-2 break-words flex-1" title={unit.title}>
              <HighlightedText text={unit.title || "Untitled"} query={searchQuery} testid={`ku-title-hl-${unit.id}`} />
            </h3>
            {unit.type === "qa" && (
              <button
                type="button"
                onClick={handleQuickRegenTitle}
                disabled={regenTitling}
                title={tr("Régénérer un titre synthétique (IA)", "Regenerate a short title (AI)")}
                data-testid={`quick-regen-title-${unit.id}`}
                className="flex-shrink-0 px-1.5 py-0.5 rounded text-sm hover:bg-purple-100 text-purple-600 disabled:opacity-50 transition-colors"
              >
                {regenTitling ? "⏳" : "✨"}
              </button>
            )}
          </div>
        </div>
        <CaretRight size={18} className="text-zinc-300 flex-shrink-0" />
      </div>
      
      <div className="mb-3">
        {renderContent()}
      </div>
      
      <div className="flex items-center gap-4 text-xs text-zinc-600">
        {/* Vincent #135 (Eric 2026-05-13) — Référence vidéo cliquable :
            ouvre la vidéo source à l'horodatage exact de la connaissance.
            Onglet déterminé par le type pour atterrir au bon endroit. */}
        {unit.video_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const start = unit.timestamps?.start;
              const t = start != null ? `&t=${Math.max(0, Math.floor(start))}` : "";
              const tab = unit.type === "qa" ? "qa"
                : unit.type === "procedure" || unit.type === "process" ? "procedures"
                : unit.type === "script" ? "scripts"
                : unit.type === "objection" || unit.type === "rebuttal" ? "objections"
                : "knowledge";
              navigate(`/dashboard/videos/${unit.video_id}?tab=${tab}${t}`);
            }}
            className="flex items-center gap-1 hover:text-[#002FA7] hover:underline transition-colors"
            data-testid={`ref-video-link-${unit.id}`}
            title={lang === "fr" ? "Ouvrir la vidéo à ce moment" : "Open video at this moment"}
          >
            <VideoCamera size={14} />
            <span className="truncate max-w-[200px]">{videoTitle}</span>
            {unit.timestamps?.start != null && (
              <span className="inline-flex items-center gap-0.5 text-[11px] font-mono text-zinc-500">
                <Clock size={12} />
                {formatTimestamp(unit.timestamps.start)}
              </span>
            )}
          </button>
        ) : (
          <span className="flex items-center gap-1 italic text-zinc-500" data-testid={`ref-video-missing-${unit.id}`}>
            <VideoCamera size={14} /> {lang === "fr" ? "vidéo introuvable" : "video missing"}
          </span>
        )}
        {/* Vincent B546 (Eric 2026-07-27) — Coeur favori déplacé en HAUT À
            GAUCHE de la carte (voir bloc plus haut). Ancien emplacement bas-
            droit retiré selon spec Vincent 24/07/2026. */}
      </div>
    </div>
  );
}

// Vincent bug #75 (2026-05-07/08) — Exported nommément pour réutilisation
// dans VideoDetailPage > onglet Q&R (modal d'édition unifié avec base de connaissance).
